import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The sheet every phone-first form sits in (fleet step 3, 2026-08-29): a
 * backdrop, a panel that rises from the bottom (centred on a wide screen),
 * a header with the title and Close, a body that scrolls, and a footer that
 * stays put — so the buttons are never below the fold on a phone keyboard.
 *
 * Dismissal: Escape, the backdrop, Close — none while `busy`, so a write in
 * flight cannot be abandoned by a stray tap. Focus goes to the first field
 * on open and back to whatever opened the sheet on close; body scroll is
 * held while it is up.
 *
 * The Escape listener sits on the document in the bubble phase, so a child
 * that has already used the key (a combobox shutting its list) stops it
 * with `e.stopPropagation()`; the ConfirmDialog's capture-phase listener
 * still wins over this one when it is up.
 */
export function BottomSheet({ title, label, onClose, busy = false, wide = false, children, footer }: {
  title: string;
  /** Accessible name when the visible title is not it (e.g. "Move Komatsu PC1100"). */
  label?: string;
  onClose: () => void;
  /** A write is in flight: nothing dismisses the sheet. */
  busy?: boolean;
  /**
   * Room for a table on a desk (OnStar import, 2026-10-07: the preview has
   * five columns). A phone gets the full width either way, so what the body
   * holds must fit it — the OnStar preview stacks its rows below sm.
   */
  wide?: boolean;
  children: ReactNode;
  /** The buttons — rendered below the scrolling body, always visible. */
  footer?: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Callers pass `onClose` inline; read it through a ref so the document
  // listener binds once per open (and per busy flip), not once per render.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  // Focus in on open, back out on close; body scroll held meanwhile.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const first = bodyRef.current?.querySelector<HTMLElement>(FOCUSABLE) ?? panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      if (opener && opener.isConnected) opener.focus();
    };
  }, []);

  useEffect(() => {
    if (busy) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCloseRef.current();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy]);

  return (
    <div
      // Michael, 2026-09-17 ("the top of that window is cut off"): the panel is
      // capped at the viewport minus the navbar, but centring it in the FULL
      // viewport still slid its top half-a-bar under the fixed NADC navbar
      // (z-index 1000 beats every z-50 overlay). Pad the backdrop by the bar's
      // height while the bar is on <body>, so centring happens below it.
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center [body.has-nadc-navbar_&]:pt-[var(--nadc-nav-height,80px)]"
      onClick={busy ? undefined : onClose}
      data-testid="bottom-sheet-backdrop"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label ?? title}
        // On a desk the cap is the viewport less a 1rem margin top and bottom.
        // While the fixed NADC bar is on the page it is the viewport minus the
        // bar — never a panel whose header hides behind it. Unscoped, that
        // took the bar's 80px off every sheet once the bar went (2026-10-10).
        className={`flex max-h-[85vh] max-h-[85dvh] w-full ${wide ? 'max-w-3xl' : 'max-w-md'} flex-col rounded-t-2xl bg-graphite-900 p-5 sm:max-h-[calc(100dvh-2rem)] sm:[body.has-nadc-navbar_&]:max-h-[calc(100dvh-var(--nadc-nav-height))] sm:rounded-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex shrink-0 items-center justify-between">
          {/* min-w-0 + break-words (Docuvault review 2026-10-10): a title with a
              J: file name in it is one long word, and a flex item is never
              narrower than its longest word — Close went off the screen. */}
          <h2 className="min-w-0 break-words text-lg font-semibold text-graphite-100">{title}</h2>
          {/* 44px target (operator UI 2026-09-17): the X is tapped with gloves on
              the Hours and Inspect sheets, and every sheet gains the same. */}
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close" className="-mr-2 flex min-h-touch min-w-touch items-center justify-center text-graphite-400 hover:text-graphite-100 disabled:opacity-50">
            <X className="h-6 w-6" />
          </button>
        </div>
        <div ref={bodyRef} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pb-1">
          {children}
        </div>
        {footer && <div className="flex shrink-0 gap-2 pt-3">{footer}</div>}
      </div>
    </div>
  );
}
